import { isEntitled, packsFromEntitlements, type EntitlementLike, type EntitlementSource } from "./billing";
import type { Plan } from "./enums";
import { FREE_LIMITS, type PremiumFeature } from "./free-tier";

/**
 * What each tier may hold. Quoted verbatim in the Terms (§3–4) and the pricing
 * page, enforced by the API (PlanService, WeddingService, FileService,
 * TemplateService). Change a number here and both follow — never in prose.
 *
 * The ceilings exist because three things cost real money and can be multiplied
 * without limit by a determined account: active weddings (D1 rows), stored bytes
 * (R2) and, once those features ship, outbound email and AI calls. Everything
 * else is a rounding error. Item caps per section (`items`) stay a free-plan
 * device only: a paid plan is "unlimited" there, bounded by storage and fair use.
 *
 * Caps gate NEW inserts only. Existing rows are always readable, editable,
 * deletable and exportable — on downgrade too. That promise is on the pricing
 * page and in the Terms; do not weaken it here.
 */
export const MB = 1_048_576;
export const GB = 1024 * MB;

export interface PlanLimits {
  /** Per-section caps for new inserts; `null` = unlimited (bounded by storage + fair use). */
  items: Readonly<Record<PremiumFeature, number>> | null;
  /** Weddings the account may OWN that are not archived (Free: total, see `countsArchived`). */
  activeWeddings: number;
  /** When true, archived weddings count too — a free account cannot cycle through weddings by archiving. */
  countsArchived: boolean;
  /** Bytes of photos + contract PDFs across every wedding the account owns. */
  storageBytes: number;
  /** New weddings per rolling 24 h, on top of the cap (stops scripted churn). */
  weddingsPerDay: number;
  /** Reusable checklist/budget/timeline templates the account may keep (Pro feature). */
  templates: number;
  /** Team seats in a shared workspace — reserved; the organisation model is not built yet. */
  seats: number;
  /** Per month, once email reminders ship; `null` = custom / pooled. */
  emailsPerMonth: number | null;
  /** Per month, once the assistant ships; `null` = custom / pooled. */
  aiMessagesPerMonth: number | null;
  /** Live, read-only run-sheet links for vendors and helpers (wedding perk, bound to the owner's plan). */
  runSheetShare: boolean;
}

export const PLAN_LIMITS: Readonly<Record<Plan, PlanLimits>> = {
  free: {
    items: FREE_LIMITS,
    activeWeddings: 1,
    countsArchived: true,
    storageBytes: 250 * MB,
    weddingsPerDay: 1,
    templates: 0,
    seats: 0,
    emailsPerMonth: 0,
    aiMessagesPerMonth: 0,
    runSheetShare: false,
  },
  premium: {
    items: null,
    activeWeddings: 3,
    countsArchived: false,
    storageBytes: 5 * GB,
    weddingsPerDay: 3,
    templates: 0,
    seats: 0,
    emailsPerMonth: 500,
    aiMessagesPerMonth: 100,
    runSheetShare: true,
  },
  pro: {
    items: null,
    activeWeddings: 15,
    countsArchived: false,
    storageBytes: 50 * GB,
    weddingsPerDay: 5,
    templates: 50,
    seats: 3,
    emailsPerMonth: 5_000,
    aiMessagesPerMonth: 500,
    runSheetShare: true,
  },
  enterprise: {
    items: null,
    activeWeddings: 100,
    countsArchived: false,
    storageBytes: 500 * GB,
    weddingsPerDay: 10,
    templates: 200,
    seats: 10,
    emailsPerMonth: null,
    aiMessagesPerMonth: null,
    runSheetShare: true,
  },
};

/** Active weddings added per Pro capacity pack (a quantity item on the Pro subscription). */
export const PACK_WEDDINGS = 10;
/** Sanity ceiling on packs; above this the account should be on Business. */
export const MAX_PACKS = 20;

/** Plan-level features that are not item caps; used in error details and upgrade prompts. */
export type PlanFeature = "activeWeddings" | "weddingsPerDay" | "storage" | "templates" | "emails";

export function activeWeddingLimit(plan: Plan, packs = 0): number {
  const base = PLAN_LIMITS[plan].activeWeddings;
  return plan === "pro" ? base + Math.max(0, Math.min(MAX_PACKS, packs)) * PACK_WEDDINGS : base;
}

/** One entitlement row as the active-wedding ceiling reads it: which store sold it, and whether it still grants. */
export interface CeilingEntitlement extends EntitlementLike {
  source: EntitlementSource | string;
}

/**
 * The active-wedding ceiling the server enforces; `null` = none. The one rule behind every path that adds an active
 * wedding (create, unarchive, hand-off; PlanService passes it to `checkWeddingInsert` / `checkUnarchive`) and behind
 * what `GET /me/usage` and `GET /me/billing` report.
 *
 * An entitled App Store purchase (`source: "apple"`: a subscription, the one-time unlock or the grandfathered cohort)
 * keeps what Premium has always allowed in the Apple apps: no cap on active weddings, whatever else the account holds.
 * It also needs a paid `user.plan`, so a row the plan has not caught up with never lifts a Free account (fail closed).
 * Every other account keeps PLAN_LIMITS (tier + Pro packs). The daily velocity, storage and every other limit stay the
 * plan's own.
 */
export function activeWeddingCeiling(plan: Plan, entitlements: readonly CeilingEntitlement[]): number | null {
  if (plan !== "free" && entitlements.some((e) => e.source === "apple" && isEntitled(e))) return null;
  return activeWeddingLimit(plan, packsFromEntitlements(entitlements));
}

/** What the active-wedding ceiling counts (`AccountUsageDTO.weddings`): the active ones, and archived ones too on Free. */
export function weddingsCounted(w: { active: number; archived: number; countsArchived: boolean }): number {
  return w.active + (w.countsArchived ? w.archived : 0);
}

export function storageLimit(plan: Plan): number {
  return PLAN_LIMITS[plan].storageBytes;
}

export interface WeddingCountSnapshot {
  /** Owned, not deleted, not archived. */
  active: number;
  /** Owned, not deleted, archived. */
  archived: number;
  /** Owned weddings created in the last 24 hours (archived or not). */
  createdLast24h: number;
}

export type WeddingInsertVerdict =
  | { ok: true }
  | { ok: false; feature: "activeWeddings" | "weddingsPerDay"; limit: number };

/**
 * May this account own one more active wedding right now? Pure so the client can
 * show the prompt before the round-trip; the server is the gate. `ceiling` is the
 * account's `activeWeddingCeiling` (`null` = none); left out, it is the tier's own
 * cap, never more. The daily velocity applies either way.
 */
export function checkWeddingInsert(
  plan: Plan, packs: number, s: WeddingCountSnapshot, ceiling: number | null = activeWeddingLimit(plan, packs),
): WeddingInsertVerdict {
  const limits = PLAN_LIMITS[plan];
  const counted = s.active + (limits.countsArchived ? s.archived : 0);
  if (ceiling !== null && counted >= ceiling) return { ok: false, feature: "activeWeddings", limit: ceiling };
  if (s.createdLast24h >= limits.weddingsPerDay) return { ok: false, feature: "weddingsPerDay", limit: limits.weddingsPerDay };
  return { ok: true };
}

/** Unarchiving needs headroom like creating does (same `ceiling` rule), but never counts against the daily velocity. */
export function checkUnarchive(
  plan: Plan, packs: number, s: Pick<WeddingCountSnapshot, "active" | "archived">, ceiling: number | null = activeWeddingLimit(plan, packs),
): WeddingInsertVerdict {
  if (ceiling !== null && s.active >= ceiling) return { ok: false, feature: "activeWeddings", limit: ceiling };
  return { ok: true };
}

export function checkStorage(plan: Plan, usedBytes: number, addBytes: number): { ok: true } | { ok: false; feature: "storage"; limit: number } {
  const limit = storageLimit(plan);
  if (addBytes > 0 && usedBytes + addBytes > limit) return { ok: false, feature: "storage", limit };
  return { ok: true };
}

/** Human-sized bytes for prompts and the Account page ("4.2 GB"). */
export function formatBytes(n: number): string {
  if (n >= GB) return `${(n / GB).toFixed(n >= 10 * GB ? 0 : 1)} GB`;
  if (n >= MB) return `${Math.round(n / MB)} MB`;
  return `${Math.max(0, Math.round(n / 1024))} KB`;
}

/** The lowest tier whose limits satisfy `pred` — for "this needs Premium" answers computed from PLAN_LIMITS, never a literal. */
export function minPlanWith(pred: (l: PlanLimits) => boolean): Plan | null {
  for (const p of ["free", "premium", "pro", "enterprise"] as const) if (pred(PLAN_LIMITS[p])) return p;
  return null;
}
